/**
 * Upload helper that survives a flaky connection.
 *
 * Tries the live multipart upload first. On network failure (no response
 * received — typical offline / tunnel-lost scenario) the file Blob and
 * metadata are stuffed into the IndexedDB queue and the sync worker will
 * replay it once we're online again.
 *
 * Returns either:
 *   { ok: true, record }   — when the live upload succeeded
 *   { ok: true, queued: true } — when it was queued offline
 *
 * Throws for non-network errors (e.g. 4xx from server) so callers can
 * surface the real validation message.
 */
import { api } from "@/lib/api";
import { enqueue } from "@/lib/offlineDb";

export async function uploadFileOffline({ projectId, file, fields = {}, label = "", timeout = 120_000 }) {
  // Same id on the live attempt and every queued replay → server dedupes.
  const uploadId = fields.client_upload_id ||
    (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  fields = { ...fields, client_upload_id: uploadId };
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") fd.append(k, v);
  });
  fd.append("file", file);
  try {
    const r = await api.post(`/projects/${projectId}/upload`, fd, { timeout });
    return { ok: true, record: r.data };
  } catch (err) {
    // Transient failures that the sync worker is better equipped to
    // handle than a user-facing toast:
    //   - no response object = browser-level network failure (true
    //     offline, DNS, CORS preflight blocked, airplane mode)
    //   - 429 = upstream object storage rate-limited (busy minute)
    //   - 503 = our own upload route surfacing a storage-busy signal
    //           after its retry budget was exhausted
    //   - 502 / 504 = transient edge/gateway hiccups
    // Any of these: queue to IndexedDB. The sync queue already retries
    // 429 and 5xx on its own schedule, so the photo makes it up as
    // soon as capacity frees up — the field crew sees "queued" instead
    // of losing the capture.
    const status = err?.response?.status;
    const isNetwork = !err?.response;
    const isTransient = status === 429 || status === 502 || status === 503 || status === 504;
    if (!isNetwork && !isTransient) throw err;
    const queueId = await enqueue({
      kind: "upload",
      projectId,
      formFields: fields,
      file,
      filename: file.name || "upload.bin",
      label,
      contentType: file.type || "application/octet-stream",
    });
    window.dispatchEvent(new CustomEvent("f2:queue-changed"));
    return { ok: true, queued: true, queueId };
  }
}
